import { Controller } from "@hotwired/stimulus";
import "./component.scss";

export default class extends Controller {
  static targets = [
    "privateBtn",
    "businessBtn",
    "billingFields",
    "billingForm",
    "businessAccountField",
    "taxIdField",
    "taxIdLabel",
    "taxIdInput",
    "countrySelect",
    "customerTypeToggle",
    "b2bInfoDialog",
    "expressCheckoutWrapper",
    "expressCheckoutOverlay",
    "expressCheckoutHint",
    "withdrawalNotice",
    "withdrawalCheckboxSection",
    "withdrawalCheckbox"
  ];

  static values = {
    isBusiness: Boolean,
    forceBusiness: Boolean,
    b2bOnlyCountries: Array,
    taxIdLabelDe: String,
    taxIdLabelCh: String,
    taxIdLabelEu: String,
    taxIdLabelB2b: String,
    taxIdPlaceholders: Object,
    activeButtonClass: String,
    communityB2bOnly: Boolean,
    privateProfileCountryCode: String,
    requiresWithdrawalCheckbox: Boolean
  };

  connect() {
    this.expressCheckoutAvailable = null;

    if (this.communityB2bOnlyValue) {
      this.forceBusinessValue = true;
      this.isBusinessValue = true;
    }
    this.updateUI();
  }

  expressCheckoutReady(event) {
    this.expressCheckoutAvailable = event.detail.available;
    this.updateExpressCheckoutState();
  }

  billingCountryPersisted(event) {
    const countryCode = event.detail?.countryCode;
    if (!countryCode) return;

    this.privateProfileCountryCodeValue = countryCode;

    if (!(this.isBusinessValue || this.forceBusinessValue)) {
      this.#dispatchBillingContextChanged();
    }
  }

  selectPrivate() {
    if (this.forceBusinessValue) {
      this.showB2bInfoDialog();
      return;
    }
    this.isBusinessValue = false;
    this.updateUI();
  }

  showB2bInfoDialog() {
    if (this.hasB2bInfoDialogTarget) {
      this.b2bInfoDialogTarget.showModal();
    }
  }

  closeB2bInfoDialog() {
    if (this.hasB2bInfoDialogTarget) {
      this.b2bInfoDialogTarget.close();
    }
  }

  selectBusiness() {
    this.isBusinessValue = true;
    this.updateUI();
  }

  billingChanged() {
    this.updateTaxIdField();
    this.updateExpressCheckoutState();
    this.#dispatchBillingContextChanged();
  }

  withdrawalCheckboxChanged(event) {
    // Sync all withdrawal checkboxes (mobile + desktop) to the same state
    const checked = event.target.checked;
    this.withdrawalCheckboxTargets.forEach(cb => { cb.checked = checked; });
    this.updateExpressCheckoutState();
  }

  updateUI() {
    const isBusiness = this.isBusinessValue || this.forceBusinessValue;

    // Update toggle buttons
    this.privateBtnTarget.classList.toggle(
      this.activeButtonClassValue,
      !isBusiness
    );
    this.businessBtnTarget.classList.toggle(
      this.activeButtonClassValue,
      isBusiness
    );

    // Update slider position
    if (this.hasCustomerTypeToggleTarget) {
      this.customerTypeToggleTarget.dataset.position = isBusiness ? "1" : "0";
    }

    // Show/hide billing fields
    this.billingFieldsTarget.hidden = !isBusiness;

    // Show withdrawal notice (text-only, below checkbox threshold) for private consumers
    if (this.hasWithdrawalNoticeTarget) {
      this.withdrawalNoticeTarget.hidden = isBusiness;
    }

    // Show/hide withdrawal checkbox sections (mobile + desktop, at or above checkbox threshold) for private consumers
    if (this.hasWithdrawalCheckboxSectionTarget) {
      this.withdrawalCheckboxSectionTargets.forEach(section => { section.hidden = isBusiness; });
      // Uncheck all when switching to business (prevent stale state)
      if (isBusiness) {
        this.withdrawalCheckboxTargets.forEach(cb => { cb.checked = false; });
      }
    }

    // Update hidden field
    if (this.hasBusinessAccountFieldTarget) {
      this.businessAccountFieldTarget.value = isBusiness ? "1" : "0";
    }

    // Update tax ID field (label, placeholder, hint) based on country
    this.updateTaxIdField();
    this.updateExpressCheckoutState();
    this.#dispatchBillingContextChanged();
  }

  countryChanged() {
    const country = this.countrySelectTarget?.value;
    const b2bCountries = this.b2bOnlyCountriesValue || [];

    if (b2bCountries.includes(country) || this.communityB2bOnlyValue) {
      this.forceBusinessValue = true;
      this.isBusinessValue = true;
      this.updateUI();
    } else {
      this.forceBusinessValue = false;
      this.updateUI();
    }
  }

  updateTaxIdField() {
    if (!this.hasCountrySelectTarget) return;

    const country = this.countrySelectTarget.value;
    const b2bCountries = this.b2bOnlyCountriesValue || [];
    const placeholders = this.taxIdPlaceholdersValue || {};

    let label, placeholder, required;
    if (country === "CH") {
      label = this.taxIdLabelChValue;
      placeholder = placeholders[country] || "CHE-123.456.789";
      required = true;
    } else if (country === "DE") {
      label = this.taxIdLabelDeValue;
      placeholder = placeholders[country] || "DE123456789";
      required = false;
    } else if (b2bCountries.includes(country)) {
      label = this.taxIdLabelB2bValue || "Tax ID*";
      placeholder = placeholders[country] || "";
      required = true;
    } else {
      label = this.taxIdLabelEuValue;
      placeholder = placeholders[country] || `${country}123456789`;
      required = true;
    }

    if (this.hasTaxIdLabelTarget) {
      this.taxIdLabelTarget.textContent = label;
    }

    if (this.hasTaxIdInputTarget) {
      this.taxIdInputTarget.placeholder = placeholder;
      this.taxIdInputTarget.required = required;
    }
  }

  updateExpressCheckoutState() {
    if (!this.hasExpressCheckoutWrapperTarget) return;

    const businessCheckout = !this.billingFieldsTarget.hidden;
    const isBusiness = this.isBusinessValue || this.forceBusinessValue;

    if (this.expressCheckoutAvailable === false) {
      this.expressCheckoutWrapperTarget.hidden = true;
      this.#setExpressCheckoutDisabled(false);
      return;
    }

    this.expressCheckoutWrapperTarget.hidden = false;

    const billingBlocking = businessCheckout && !this.#billingDetailsComplete();
    const withdrawalBlocking = this.requiresWithdrawalCheckboxValue && !isBusiness && this.hasWithdrawalCheckboxTarget && !this.withdrawalCheckboxTargets.some(cb => cb.checked);

    this.#setExpressCheckoutDisabled(billingBlocking || withdrawalBlocking);

    // Show billing hint when billing details are incomplete
    if (this.hasExpressCheckoutHintTarget) {
      this.expressCheckoutHintTarget.hidden = !billingBlocking;
    }
  }

  #setExpressCheckoutDisabled(disabled) {
    this.expressCheckoutWrapperTarget.dataset.disabled = disabled ? "true" : "false";
    this.expressCheckoutWrapperTarget.setAttribute("aria-disabled", disabled.toString());

    if (this.hasExpressCheckoutOverlayTarget) {
      this.expressCheckoutOverlayTarget.hidden = !disabled;
    }

    if (this.hasExpressCheckoutHintTarget) {
      this.expressCheckoutHintTarget.hidden = !disabled;
    }
  }

  #billingDetailsComplete() {
    if (!this.hasBillingFormTarget || this.billingFieldsTarget.hidden) return true;

    const requiredSelectors = [
      '[name="user[first_name]"]',
      '[name="user[last_name]"]',
      '[name="user[billing_address_line_one]"]',
      '[name="user[billing_address_postal_code]"]',
      '[name="user[billing_address_city]"]',
      '[name="user[billing_address_country_code]"]'
    ];

    const requiredFieldsFilled = requiredSelectors.every((selector) => {
      const field = this.billingFormTarget.querySelector(selector);
      return field && field.value.trim().length > 0;
    });

    if (!requiredFieldsFilled) return false;

    const country = this.countrySelectTarget?.value;
    if (country !== "DE" && this.hasTaxIdInputTarget) {
      return this.taxIdInputTarget.value.trim().length > 0;
    }

    return true;
  }

  #dispatchBillingContextChanged() {
    const isBusiness = this.isBusinessValue || this.forceBusinessValue;
    const countryCode = isBusiness
      ? (this.hasCountrySelectTarget ? (this.countrySelectTarget.value || null) : null)
      : (this.privateProfileCountryCodeValue || null);

    this.element.dispatchEvent(
      new CustomEvent("checkout-payment-form:billing-context-changed", {
        bubbles: true,
        detail: {
          businessAccount: isBusiness,
          countryCode,
        },
      })
    );
  }
}
